<template>
    <main class="not-found-page">
        <div class="not-found-shell">
            <header class="not-found-header" aria-label="Error page header">
                <router-link class="not-found-brand" to="/builder/home" aria-label="Go to GixCore builder">
                    <span class="not-found-wordmark">Gix<span>Core</span></span>
                </router-link>
                <span class="not-found-state">
                    <span class="not-found-state-dot" aria-hidden="true"></span>
                    404 / Route not found
                </span>
            </header>

            <section class="not-found-layout" aria-labelledby="not-found-title">
                <div class="not-found-copy-panel">
                    <span class="not-found-kicker">404 / Route not found</span>
                    <div class="not-found-title-row">
                        <span class="not-found-mark" aria-hidden="true"></span>
                        <h1 style="font-size: 2.5rem !important; " id="not-found-title">That route is outside the build.</h1>
                    </div>
                    <p class="not-found-lead">
                        The link may have moved, or the route points to a builder screen GixCore does not publish.
                        Jump back into the builder or use one of the supported paths below.
                    </p>

                    <div class="not-found-actions" aria-label="Recovery actions">
                        <router-link to="/builder/home" class="not-found-button not-found-button-primary">
                            Open builder
                        </router-link>
                        <router-link to="/" class="not-found-button">
                            Go home
                        </router-link>
                        <router-link to="/users/login" class="not-found-button">
                            Log in
                        </router-link>
                    </div>
                </div>

                <aside class="not-found-diagnostic" aria-label="Route diagnostic">
                    <div class="not-found-diagnostic-head">
                        <span>Route check</span>
                        <strong>404</strong>
                    </div>

                    <div v-if="currentPath" class="not-found-route">
                        <span>Requested path</span>
                        <code>{{ currentPath }}</code>
                    </div>

                    <dl class="not-found-facts">
                        <div>
                            <dt>Builder</dt>
                            <dd>/builder/home</dd>
                        </div>
                        <div>
                            <dt>Login</dt>
                            <dd>/users/login</dd>
                        </div>
                        <div>
                            <dt>Catalog</dt>
                            <dd>gixcore.com/parts</dd>
                        </div>
                    </dl>

                    <div class="not-found-recovery">
                        <router-link to="/builder/home">
                            <span>Build checklist</span>
                            <small>Return to the active PC builder</small>
                        </router-link>
                        <a href="https://gixcore.com/parts" rel="noopener">
                            <span>Parts library</span>
                            <small>Browse supported catalog routes</small>
                        </a>
                    </div>
                </aside>
            </section>
        </div>
    </main>
</template>

<script>
export default {
    name: 'NotFound',
    metaInfo: {
        title: 'Page Not Found | GixCore',
        meta: [
            { name: 'robots', content: 'noindex, nofollow' },
        ],
    },
    computed: {
        currentPath() {
            return this.$route && this.$route.fullPath;
        },
    },
};
</script>

<style scoped>
.not-found-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    overflow: hidden;
    padding: clamp(72px, 9vh, 104px) 24px 48px;
    background:
        linear-gradient(90deg, rgba(4, 6, 10, 0.95), rgba(8, 12, 18, 0.82) 48%, rgba(4, 6, 10, 0.96)),
        linear-gradient(180deg, rgba(8, 10, 15, 0.18), rgba(8, 10, 15, 0.98)),
        url('../assets/img/builder-bg.png') center / cover fixed no-repeat,
        #080a0f;
    color: #cfd2da;
    font-family: 'Roboto Condensed', 'Roboto', Arial, sans-serif;
}

.not-found-page::before,
.not-found-page::after {
    position: absolute;
    inset: 0;
    content: '';
    pointer-events: none;
}

.not-found-page::before {
    z-index: -2;
    background:
        linear-gradient(rgba(25, 151, 198, 0.06) 1px, transparent 1px),
        linear-gradient(90deg, rgba(25, 151, 198, 0.045) 1px, transparent 1px);
    background-size: 64px 64px;
    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.78), transparent 72%);
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.78), transparent 72%);
}

.not-found-page::after {
    z-index: -1;
    background:
        linear-gradient(118deg, rgba(25, 151, 198, 0.1), transparent 36%, rgba(228, 216, 54, 0.035) 74%, transparent),
        linear-gradient(180deg, rgba(8, 10, 15, 0.05), #080a0f);
}

.not-found-shell {
    position: relative;
    z-index: 1;
    width: min(1120px, 100%);
    margin: 0 auto;
}

.not-found-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 24px;
}

.not-found-brand {
    display: inline-flex;
    align-items: center;
    color: #f4f8fb;
    text-decoration: none;
}

.not-found-wordmark {
    color: #f4f8fb;
    font-family: 'Audiowide', 'Aldrich', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.72rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1;
    white-space: nowrap;
}

.not-found-wordmark span {
    color: #1997c6;
}

.not-found-state {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-height: 34px;
    padding: 0 13px;
    border: 1px solid rgba(25, 151, 198, 0.48);
    border-radius: 999px;
    background: rgba(8, 10, 15, 0.62);
    color: #b9c6d8;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.not-found-state-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #16b4ed;
    box-shadow: 0 0 18px rgba(22, 180, 237, 0.72);
}

.not-found-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 0.56fr);
    gap: 18px;
    align-items: stretch;
}

.not-found-copy-panel,
.not-found-diagnostic {
    border: 1px solid rgba(67, 72, 87, 0.86);
    border-radius: 10px;
    background:
        linear-gradient(145deg, rgba(25, 151, 198, 0.12), transparent 42%),
        linear-gradient(180deg, rgba(18, 23, 33, 0.9), rgba(8, 10, 15, 0.76)),
        rgba(8, 10, 15, 0.7);
    box-shadow:
        0 24px 80px rgba(0, 0, 0, 0.36),
        inset 0 0 0 1px rgba(255, 255, 255, 0.018);
}

.not-found-copy-panel {
    padding: clamp(24px, 3vw, 36px);
}

.not-found-kicker {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    margin-bottom: 0.95rem;
    padding: 0 0.65rem;
    border: 1px solid rgba(67, 72, 87, 0.74);
    border-radius: 6px;
    background: rgba(8, 10, 15, 0.44);
    color: #1997c6;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
}

.not-found-title-row {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 18px;
    align-items: start;
    margin-top: 12px;
    align-items: center;
}

.not-found-mark {
    position: relative;
    display: grid;
    place-items: center;
    width: 56px;
    aspect-ratio: 1;
    border: 1px solid rgba(22, 180, 237, 0.72);
    border-radius: 8px;
    background:
        linear-gradient(180deg, rgba(25, 151, 198, 0.2), rgba(25, 151, 198, 0.06)),
        rgba(8, 10, 15, 0.64);
    box-shadow:
        0 0 24px rgba(25, 151, 198, 0.14),
        inset 0 0 18px rgba(25, 151, 198, 0.1);
}

.not-found-mark::before {
    width: 28px;
    height: 28px;
    background: #16b4ed;
    content: '';
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M9 6H5a2 2 0 0 0-2 2v11h11a2 2 0 0 0 2-2v-4'/%3E%3Cpath d='M14 3h7v7'/%3E%3Cpath d='M21 3 10 14'/%3E%3Cpath d='m3 3 18 18'/%3E%3C/svg%3E") center / contain no-repeat;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M9 6H5a2 2 0 0 0-2 2v11h11a2 2 0 0 0 2-2v-4'/%3E%3Cpath d='M14 3h7v7'/%3E%3Cpath d='M21 3 10 14'/%3E%3Cpath d='m3 3 18 18'/%3E%3C/svg%3E") center / contain no-repeat;
}

.not-found-copy-panel h1 {
    max-width: 640px;
    margin: 0;
    color: #f4f8fb;
    font-size: clamp(2rem, 3.7vw, 3.55rem);
    font-weight: 600;
    letter-spacing: 0;
    line-height: 1.04;
}

.not-found-lead {
    max-width: 650px;
    margin: 20px 0 0;
    color: #b9c3d2;
    font-size: clamp(0.98rem, 1.08vw, 1.06rem);
    line-height: 1.66;
}

.not-found-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 26px;
}

.not-found-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 142px;
    min-height: 42px;
    padding: 0 1.15rem;
    border: 1px solid rgba(67, 72, 87, 0.86);
    border-radius: 6px;
    background: rgba(8, 10, 15, 0.32);
    color: #d5dbe6;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.3px;
    line-height: 1;
    text-decoration: none;
    text-transform: uppercase;
    transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease;
}

.not-found-button:hover,
.not-found-button:focus {
    border-color: rgba(25, 151, 198, 0.56);
    background: rgba(25, 151, 198, 0.08);
    color: #ffffff;
    text-decoration: none;
}

.not-found-button-primary {
    border-color: #16b4ed;
    background: linear-gradient(180deg, rgba(25, 151, 198, 0.4), rgba(12, 66, 88, 0.72));
    color: #f4f8fb;
    box-shadow:
        0 0 26px rgba(25, 151, 198, 0.18),
        inset 0 0 18px rgba(25, 151, 198, 0.16);
}

.not-found-button-primary:hover,
.not-found-button-primary:focus {
    border-color: #16b4ed;
    background: linear-gradient(180deg, rgba(25, 151, 198, 0.56), rgba(13, 88, 117, 0.82));
    color: #ffffff;
    box-shadow:
        0 0 34px rgba(25, 151, 198, 0.26),
        inset 0 0 18px rgba(25, 151, 198, 0.2);
    transform: translateY(-1px);
}

.not-found-diagnostic {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
}

.not-found-diagnostic-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.not-found-diagnostic-head span,
.not-found-route span {
    color: #9db0ca;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.not-found-diagnostic-head strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    height: 32px;
    border: 1px solid rgba(228, 216, 54, 0.46);
    border-radius: 999px;
    color: #e4d836;
    font-size: 16px;
    font-weight: 700;
}

.not-found-route {
    display: grid;
    gap: 9px;
    padding: 14px;
    border: 1px solid rgba(67, 72, 87, 0.74);
    border-radius: 8px;
    background: rgba(4, 7, 12, 0.58);
}

.not-found-route code {
    display: block;
    color: #f4f8fb;
    font-family: Consolas, 'SFMono-Regular', monospace;
    font-size: 0.95rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
}

.not-found-facts {
    display: grid;
    gap: 0;
    margin: 0;
    border-top: 1px solid rgba(67, 72, 87, 0.56);
}

.not-found-facts div {
    display: grid;
    grid-template-columns: 100px minmax(0, 1fr);
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid rgba(67, 72, 87, 0.56);
}

.not-found-facts dt,
.not-found-facts dd {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
}

.not-found-facts dt {
    color: #7f899b;
    font-weight: 700;
    text-transform: uppercase;
}

.not-found-facts dd {
    min-width: 0;
    color: #f4f8fb;
    font-weight: 600;
    overflow-wrap: anywhere;
}

.not-found-recovery {
    display: grid;
    gap: 10px;
    margin-top: 2px;
}

.not-found-recovery a {
    display: grid;
    gap: 3px;
    width: 100%;
    padding: 13px 14px;
    border: 1px solid rgba(67, 72, 87, 0.78);
    border-radius: 8px;
    background: rgba(8, 10, 15, 0.44);
    color: #cfd2da;
    text-align: left;
    text-decoration: none;
    transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.not-found-recovery a:hover,
.not-found-recovery a:focus {
    border-color: rgba(25, 151, 198, 0.66);
    background: rgba(25, 151, 198, 0.1);
    color: #f4f8fb;
    text-decoration: none;
}

.not-found-recovery span {
    color: #f4f8fb;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
}

.not-found-recovery small {
    color: #8d97aa;
    font-size: 12px;
    line-height: 1.35;
}

@media screen and (max-width: 920px) {
    .not-found-page {
        padding: 72px 18px 42px;
    }

    .not-found-layout {
        grid-template-columns: 1fr;
    }

    .not-found-copy-panel h1 {
        font-size: clamp(2rem, 7vw, 3rem);
    }
}

@media screen and (max-width: 620px) {
    .not-found-header {
        align-items: flex-start;
        flex-direction: column;
        margin-bottom: 22px;
    }

    .not-found-state {
        max-width: 100%;
    }

    .not-found-copy-panel,
    .not-found-diagnostic {
        padding: 18px;
    }

    .not-found-title-row {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .not-found-mark {
        width: 58px;
    }

    .not-found-copy-panel h1 {
        font-size: clamp(1.85rem, 10vw, 2.55rem);
    }

    .not-found-lead {
        margin-top: 18px;
    }

    .not-found-actions,
    .not-found-button {
        width: 100%;
    }

    .not-found-facts div {
        grid-template-columns: 1fr;
        gap: 4px;
    }
}
</style>
